import {
  inject,
  onBeforeUnmount,
  provide,
  shallowRef,
  watch,
  type InjectionKey,
  type Ref,
  type ShallowRef,
} from 'vue'

import type { AppIconName } from '@/components/AppIcon.vue'

export interface SubPageHeaderAction {
  label: string
  icon?: AppIconName
  disabled?: boolean
  onSelect: () => void | Promise<void>
}

interface SubPageHeaderActionRegistry {
  action: ShallowRef<SubPageHeaderAction | undefined>
  register(source: Readonly<Ref<SubPageHeaderAction>>): () => void
}

const subPageHeaderActionKey: InjectionKey<SubPageHeaderActionRegistry> = Symbol('sub-page-header-action')

export function provideSubPageHeaderAction(): ShallowRef<SubPageHeaderAction | undefined> {
  const action = shallowRef<SubPageHeaderAction>()
  let currentOwner: symbol | undefined

  provide(subPageHeaderActionKey, {
    action,
    register(source) {
      const owner = Symbol('sub-page-header-action-owner')
      currentOwner = owner
      const stop = watch(source, value => {
        if (currentOwner === owner) action.value = value
      }, { immediate: true })

      return () => {
        stop()
        if (currentOwner === owner) {
          currentOwner = undefined
          action.value = undefined
        }
      }
    },
  })

  return action
}

export function useSubPageHeaderAction(source: Readonly<Ref<SubPageHeaderAction>>): void {
  const registry = inject(subPageHeaderActionKey, undefined)
  const unregister = registry?.register(source)
  onBeforeUnmount(() => unregister?.())
}
